import type { QueryKey, UseQueryOptions } from '@tanstack/react-query';

import { settings } from '@/_config';

import { fetchBaseQuery } from './tanstack-api';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from './tanstack-api';

import baseQueryWithReAuth from '../helper/base-query-with-auth';

export type TQueryHookOptions<TData = unknown, TQueryKey extends QueryKey = QueryKey> = Omit<
  UseQueryOptions<TData, FetchBaseQueryError, TData, TQueryKey>,
  'queryKey' | 'queryFn'
>;

const atpBaseQuery = baseQueryWithReAuth({ baseUrl: settings.baseURL[0], appName: 'ATP', authRequired: true });
const aodBaseQuery = baseQueryWithReAuth({
  baseUrl: settings.baseURL[1],
  appName: 'ANSWER_ON_DEMAND',
  authRequired: true,
});
const instructorBaseQuery = baseQueryWithReAuth({
  baseUrl: settings.baseURL[2],
  appName: 'INSTRUCTOR',
  authRequired: true,
});
const unAuthCustomerBaseQuery = baseQueryWithReAuth({
  baseUrl: settings.baseURL[0],
  appName: 'ATP',
  authRequired: false,
});
const unAuthInstructorBaseQuery = baseQueryWithReAuth({
  baseUrl: settings.baseURL[2],
  appName: 'INSTRUCTOR',
  authRequired: false,
});
const bookingsBaseQuery = fetchBaseQuery({
  baseUrl: 'https://api.youcanbook.me/v1/',
  prepareHeaders: (headers) => {
    headers.set(
      'authorization',
      'Basic YXRwLmRyaXZlQGFjaGlldmUtdGVzdC1wcmVwLmNvbTpha19WdUo0SkJkY1ljNkxiSlpYU1ZOQVdzTFVIOXVWVE1HOGNONzIzbVB3elJhaE1RQkRCdw=='
    );
    return headers;
  },
});

async function executeBaseQuery<TData>(
  baseQuery: BaseQueryFn,
  endpoint: string,
  args: string | FetchArgs,
  transformResponse?: (response: unknown) => TData
) {
  const response = await baseQuery(args, { endpoint }, undefined);

  if (response.error) {
    throw response.error;
  }

  const data = response.data;
  return transformResponse ? transformResponse(data) : (data as TData);
}

export async function atpRequest<TData>(
  endpoint: string,
  args: string | FetchArgs,
  transformResponse?: (response: unknown) => TData
) {
  return executeBaseQuery(atpBaseQuery, endpoint, args, transformResponse);
}

export async function customerPersistentRequest<TData>(
  endpoint: string,
  args: string | FetchArgs,
  transformResponse?: (response: unknown) => TData
) {
  return executeBaseQuery(atpBaseQuery, endpoint, args, transformResponse);
}

export async function aodRequest<TData>(
  endpoint: string,
  args: string | FetchArgs,
  transformResponse?: (response: unknown) => TData
) {
  return executeBaseQuery(aodBaseQuery, endpoint, args, transformResponse);
}

export async function instructorRequest<TData>(
  endpoint: string,
  args: string | FetchArgs,
  transformResponse?: (response: unknown) => TData
) {
  return executeBaseQuery(instructorBaseQuery, endpoint, args, transformResponse);
}

export async function unAuthCustomerRequest<TData>(
  endpoint: string,
  args: string | FetchArgs,
  transformResponse?: (response: unknown) => TData
) {
  return executeBaseQuery(unAuthCustomerBaseQuery, endpoint, args, transformResponse);
}

export async function unAuthInstructorRequest<TData>(
  endpoint: string,
  args: string | FetchArgs,
  transformResponse?: (response: unknown) => TData
) {
  return executeBaseQuery(unAuthInstructorBaseQuery, endpoint, args, transformResponse);
}

export async function bookingsRequest<TData>(
  endpoint: string,
  args: string | FetchArgs,
  transformResponse?: (response: unknown) => TData
) {
  return executeBaseQuery(bookingsBaseQuery, endpoint, args, transformResponse);
}
